import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Check, Zap, BarChart3, Layers, TrendingUp, Target } from "lucide-react";

/**
 * Shown to any signed-in C-Suite user who does not have an active paid
 * subscription. Non-admin, non-subscribed users can log in and see this
 * paywall, but every /api/platinum/* call returns 402 and each tab's data
 * loader shows this card in place of the platinum module.
 */
export default function PaywallCard() {
  return (
    <div className="max-w-3xl mx-auto py-6" data-testid="paywall-card">
      <div className="card p-8 md:p-10 text-center relative overflow-hidden">
        {/* Sheen band */}
        <div className="absolute inset-x-0 top-0 h-1" style={{ background: "linear-gradient(90deg, #7A1F2B 0%, #B03A48 50%, #E5A9B3 100%)" }} />
        <div className="w-14 h-14 rounded-full grid place-items-center text-white mx-auto mb-4" style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)" }}>
          <Sparkles size={22}/>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Executive Portal · Subscription required</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-2 text-zinc-900">Unlock the Platinum Executive Portal</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-xl mx-auto">
          The C-Suite portal is a paid experience. Choose Starter or Platinum to activate live dashboards, <b>NAVBOT</b> (your executive AI assistant), and every module below.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mt-8 text-left">
          <div className="border border-[var(--border)] rounded-sm p-5">
            <div className="flex items-baseline justify-between mb-2">
              <div className="font-display text-lg font-extrabold">Starter</div>
              <div className="font-mono text-xs text-zinc-500">$200<span className="text-zinc-400"> /mo</span></div>
            </div>
            <ul className="text-sm text-zinc-700 space-y-1.5">
              <PerkLine icon={<Layers size={13}/>}      text="Portfolio Command Center"/>
              <PerkLine icon={<Target size={13}/>}      text="Executive Scorecard (RAG)"/>
              <PerkLine icon={<BarChart3 size={13}/>}   text="SAP S/4HANA Cockpit"/>
              <PerkLine icon={<Zap size={13}/>}         text="NAVBOT — executive AI · chat"/>
            </ul>
          </div>
          <div className="border-2 rounded-sm p-5 relative" style={{ borderColor: "#7A1F2B", background: "rgba(122, 31, 43, 0.04)" }}>
            <div className="absolute -top-2 right-3 pill text-[9px]" style={{ background: "#7A1F2B", color: "#FFFFFF" }}>RECOMMENDED</div>
            <div className="flex items-baseline justify-between mb-2">
              <div className="font-display text-lg font-extrabold">Platinum</div>
              <div className="font-mono text-xs text-zinc-500">$350<span className="text-zinc-400"> /mo</span></div>
            </div>
            <ul className="text-sm text-zinc-700 space-y-1.5">
              <PerkLine icon={<Check size={13}/>}       text="Everything in Starter"/>
              <PerkLine icon={<BarChart3 size={13}/>}   text="Microsoft Dynamics 365 Cockpit"/>
              <PerkLine icon={<TrendingUp size={13}/>}  text="Value Realization Tracker"/>
              <PerkLine icon={<Sparkles size={13}/>}    text="Proactive AI Insights (Claude 4.5)"/>
              <PerkLine icon={<Zap size={13}/>}         text="NAVBOT — executive AI · chat + proactive quick-actions"/>
              <PerkLine icon={<Check size={13}/>}       text="Board Pack generator (PPT)"/>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/signup" className="btn btn-primary" data-testid="paywall-subscribe">
            Choose your plan →
          </Link>
          <Link to="/pricing" className="btn btn-secondary btn-sm" data-testid="paywall-learn-more">
            Learn more about each module
          </Link>
          <Link to="/csuite" className="text-xs text-zinc-500 hover:text-zinc-800 font-mono uppercase tracking-widest">
            or log out
          </Link>
        </div>
        <div className="mt-6 pt-6 border-t border-[var(--border)] font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          Questions before you subscribe? Email <a href="mailto:Requests@Capittance.com?subject=Executive%20Portal%20subscription%20enquiry" className="underline" data-testid="paywall-support">Requests@Capittance.com</a>
        </div>
      </div>
    </div>
  );
}

function PerkLine({ icon, text }) {
  return <li className="flex items-start gap-2"><span className="text-cyan-600 mt-0.5">{icon}</span>{text}</li>;
}
